import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faIdCard, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  digObject, generateRedirectUrl, getQueryParamsFromHash, parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import {
  Box,
  Button,
  Columns,
  LoadingModule,
  SaveFormModule,
  Tools,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useAgencyProviderPackage from '@hooks/useAgencyProviderPackage'
import useProviderPackage, { useProviderPackageForm } from '@hooks/useProviderPackage'
import { generateUrls } from '@hooks/useProvider'

import { confirmDelete } from '@functions/entity'

import EntityActivity from '@components/EntityActivity'
import EntityAssets from '@components/EntityAssets'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import LinkToEntityModule from '@components/LinkToEntityModule'

import AgencyProviderPackages from '../AgencyProviderPackages'
import Details from '../Details'
import ProviderPackageArchivedBanner from '../ProviderPackageArchivedBanner'
import ProviderPackageProducts from '../ProviderPackageProducts'

const PageContent = (props: { providerPackageId: number }) => {
  const { providerPackageId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks,
    callbacks: {
      updateProviderPackage,
    },
  } = pageContext

  const providerPackagePayload = useProviderPackage({ id: providerPackageId })
  const {
    callbacks: {
      deleteProviderPackage: deleteFn,
      launchDuplicateProviderPackageModal,
      updateProviderPackage: updateFn,
    },
    deleting,
    loaded,
    loading,
    provider,
    providerPackage,
    providerPackage: { hidden },
    updating,
  } = providerPackagePayload

  // @ts-expect-error TS2345
  const providerPackageForm = useProviderPackageForm(providerPackage)
  const { entityState, saveEnabled } = providerPackageForm

  const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

  const {
    logo: providerLogo,
    short_name: providerShortName,
    title: providerTitle,
  } = provider

  const { editProviderUrl } = generateUrls(provider)

  const providerImageUrl = digObject(providerLogo || {}, 'thumb', '')

  const updateProviderPackagePayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Provider Package Updated',
  }

  const agencyPackagePayload = useAgencyProviderPackage({ provider_package_id: providerPackageId })
  const {
    callbacks: {
      bulkDeleteAgencyProviderPackages,
    },
  } = agencyPackagePayload

  const confirmDeletePackageParams = {
    callbacks: {
      deleteFn,
    },
    strings: {
      deleteString: 'archive',
      entityName: 'Package',
    },
  }

  const confirmDeleteAllParams = {
    callbacks: {
      deleteFn: bulkDeleteAgencyProviderPackages,
    },
    options: {
      entityParams: { provider_package_id: providerPackageId },
      isBulkDelete: true,
    },
    strings: {
      confirmString: 'Are you sure? This will remove all assigned agencies, including any not currently shown.',
      deleteString: 'remove',
      entityName: 'Agencies',
    },
  }

  return (
    <>
      <Helmet>
        <title>Provider Package | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            // @ts-expect-error TS2322
            as="a"
            buttonStyle="secondaryUtility"
            href={redirect || '#/systemManager/providerPackages'}
            size="medium"
          >
            Back
          </Button>
        )}
        activeTabBarItemKey="details"
        tabBarItems={[
          // @ts-expect-error TS2741
          {
            icon: faIdCard,
            key: 'details',
            title: 'Package Details',
          },
        ]}
        title="Edit Package"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading && !loaded} times={3} />

            {loaded && hidden && (
              // @ts-expect-error TS2322
              <ProviderPackageArchivedBanner providerPackage={providerPackage} />
            )}

            {loaded && (
              <Details providerPackageForm={providerPackageForm} />
            )}

            <ProviderPackageProducts providerPackageId={providerPackageId} />

            <AgencyProviderPackages providerPackageId={providerPackageId} />

            <EntityActivity subjectId={providerPackageId} subjectType="ProviderPackage" />

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                onClick={() => updateProviderPackage(updateProviderPackagePayload)}
                size="medium"
                style={{ width: '100%' }}
              >
                Save Details
              </Button>
            </SaveFormModule>

          </Columns.Main>

          <Columns.Sidebar>

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <LinkToEntityModule
                entityTitle={providerTitle || ''}
                goToHref={`${editProviderUrl}?redirect=${generateRedirectUrl()}`}
                imageUrl={providerImageUrl}
                loading={loading}
                moduleTitle="Provider"
                secondaryText={providerShortName}
              />
            </ScopedConditionalContent>

            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faCopy} />}
                onClick={() => launchDuplicateProviderPackageModal()}
                size="medium"
              >
                Duplicate Package
              </Button>

              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                marginTop="medium"
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeleteAllParams)}
                size="medium"
              >
                Remove All Agencies
              </Button>

              {!hidden && (
                <Button
                  buttonStyle="secondaryDestroy"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  loading={deleting}
                  marginTop="medium"
                  // @ts-expect-error TS2345
                  onClick={() => confirmDelete(confirmDeletePackageParams)}
                  size="medium"
                >
                  Archive Package
                </Button>
              )}
            </Tools>

            <EntityAssets
              callbacks={callbacks}
              filters={{ attachableId: providerPackageId, attachableType: 'ProviderPackage' }}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
